import React, {
	createContext,
	useContext,
	useMemo,
	useRef,
	useState,
	useEffect,
	type ReactNode,
	type RefObject,
	useLayoutEffect,
} from 'react';
import debounce from 'lodash/debounce';

import Portal from '@atlaskit/portal';
import { layers } from '@atlaskit/theme/constants';
import { Box } from '@atlaskit/primitives/compiled';
import { cssMap, cx } from '@atlaskit/css';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import type { Snippet } from '../snippetTypes.ts';

import { calculatePreviewPosition } from './snippetPreviewHelpers.ts';
import { SnippetPopupPreviewFrame } from './SnippetPopupPreviewFrame.tsx';

const HIDE_TIMEOUT = 250;
const RENDER_DEBOUNCE_TIME = 80;

const styles = cssMap({
	previewBox: {
		position: 'fixed',
	},
	hidden: {
		opacity: 0,
		pointerEvents: 'none',
	},
});

type SnippetPopupPreviewContextType = {
	showPreview: (snippet: Snippet, rect: DOMRect) => void;
	hidePreview: () => void;
};

const SnippetPopupPreviewContext = createContext<SnippetPopupPreviewContextType>({
	showPreview: () => {},
	hidePreview: () => {},
});

export const useSnippetPopupPreview = () => useContext(SnippetPopupPreviewContext);

export const SnippetPopupPreviewProvider = ({ children }: { children: ReactNode }) => {
	const previewRef = useRef<HTMLDivElement>(null);

	const { currentSnippet, position, resumePreview, showPreview, hidePreview, shouldShowPopup } =
		useSnippetPopupPreviewController(previewRef);

	const contextValue = useMemo(() => ({ showPreview, hidePreview }), [showPreview, hidePreview]);

	return (
		<SnippetPopupPreviewContext.Provider value={contextValue}>
			<Portal zIndex={layers.tooltip()}>
				<TransparentErrorBoundary attribution={Attribution.INTEGRATIONS}>
					<Box
						ref={previewRef}
						onMouseEnter={resumePreview}
						onMouseLeave={hidePreview}
						xcss={cx(styles.previewBox, (!currentSnippet || !shouldShowPopup) && styles.hidden)}
						style={{
							top: position?.top ?? 0,
							left: position?.left ?? 0,
							opacity: shouldShowPopup ? 1 : 0,
							transition: shouldShowPopup
								? 'top 250ms linear, left 250ms linear, height 1000ms linear, opacity 100ms linear'
								: 'none',
						}}
					>
						<SnippetPopupPreviewFrame snippet={currentSnippet} />
					</Box>
				</TransparentErrorBoundary>
			</Portal>
			{children}
		</SnippetPopupPreviewContext.Provider>
	);
};

const useSnippetPopupPreviewController = (previewRef: RefObject<HTMLDivElement | null>) => {
	const [currentSnippet, setCurrentSnippet] = useState<Snippet>();
	const [itemRect, setItemRect] = useState<DOMRect>();
	const [position, setPosition] = useState<{ top: number; left: number }>();
	const [shouldShowPopup, setShouldShowPopup] = useState(false);

	const hideTimeout = useRef<number>();
	const prevPositionRef = useRef<{ top: number; left: number }>();

	const debouncedSetSnippet = useMemo(
		() =>
			debounce((snippet: Snippet, rect: DOMRect) => {
				setCurrentSnippet(snippet);
				setItemRect(rect);
			}, RENDER_DEBOUNCE_TIME),
		[],
	);

	useEffect(() => {
		return () => {
			debouncedSetSnippet.cancel();
			clearTimeout(hideTimeout.current);
		};
	}, [debouncedSetSnippet]);

	// Recalculate position after snippet renders and we have actual dimensions
	useLayoutEffect(() => {
		let frame: number;
		if (currentSnippet && previewRef.current && itemRect) {
			const newPosition = calculatePreviewPosition(
				previewRef.current.getBoundingClientRect(),
				itemRect,
			);
			setPosition(newPosition);

			// Enable transition in the next frame to avoid animation from default position
			if (!prevPositionRef.current) {
				frame = requestAnimationFrame(() => {
					setShouldShowPopup(true);
				});
			}
			prevPositionRef.current = newPosition;
		}
		return () => {
			if (frame) {
				cancelAnimationFrame(frame);
			}
		};
	}, [currentSnippet, previewRef, itemRect]);

	const resumePreview = () => {
		clearTimeout(hideTimeout.current);
		hideTimeout.current = undefined;
	};

	const showPreview = (snippet: Snippet, rect: DOMRect) => {
		resumePreview();

		const isSameSnippet = currentSnippet && currentSnippet.snippetId === snippet.snippetId;
		if (isSameSnippet) {
			return;
		}

		// Debounce for when a user hovers through snippets very quickly
		debouncedSetSnippet(snippet, rect);
	};

	const hidePreview = () => {
		if (hideTimeout.current) {
			return;
		}
		hideTimeout.current = window.setTimeout(() => {
			setCurrentSnippet(undefined);
			setItemRect(undefined);
			setPosition(undefined);
			setShouldShowPopup(false);
			hideTimeout.current = undefined;
			prevPositionRef.current = undefined;
		}, HIDE_TIMEOUT);
	};

	useEffect(() => {
		window.addEventListener('resize', hidePreview);
		return () => window.removeEventListener('resize', hidePreview);
	}, []);

	return {
		currentSnippet,
		position,
		itemRect,
		resumePreview,
		showPreview,
		hidePreview,
		shouldShowPopup,
	};
};
